import { Link, useParams } from 'react-router-dom';

import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/new/components/shadcn/dialog';

interface KimDeprecationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  subsetId: string;
}

export function KimDeprecationDialog({ open, onOpenChange, subsetId }: KimDeprecationDialogProps) {
  const { matterId } = useParams();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--alert-circle" /> The legacy Kim experience is being sunset on the 15th of June
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <h2 className="font-medium">What's changing?</h2>
            <p>On the 15th of June 2026, the legacy single-turn Kim experience will no longer be accessible.</p>
          </div>

          <div className="space-y-2">
            <h2 className="font-medium">What happens to my legacy Kim questions?</h2>
            <p>
              All of your existing Kim questions and answers will be migrated over to the new Kim experience
              automatically. No data will be lost.
            </p>
            <p>
              You won't be able to continue a legacy Kim thread in the new Kim experience; in these cases we suggest
              creating a new thread.
            </p>
            <p>
              If you have any concerns or questions, please reach out to the Wexler team at{' '}
              <a href="mailto:support@wexler.ai" className="text-blue-500">
                support@wexler.ai
              </a>
              .
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Dismiss
          </Button>
          <Button asChild>
            <Link
              to={`/matters/${matterId}/subsets/${subsetId}/new-kim`}
              onClick={() => {
                trackCustomEvent(TrackingEventsV2.KimEnableNewExperience, {
                  matterId,
                  subsetId,
                  from: 'Kim deprecation dialog',
                });
                localStorage.setItem('isUsingNewKim', 'true');
                onOpenChange(false);
              }}
            >
              Try the new Kim
              <Icon className="lucide--arrow-right" />
            </Link>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
